import React, { useCallback, useEffect, useMemo } from "react";
import { Button, Space } from "antd";
import styles from "pages/admin/objectsAndClients/objects/report/report.module.scss";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import MobileInnerWrapper from "layouts/mobileInnerWrapper/mobileInnerWrapper";
import Table, { ColumnsType } from "components/table/table";
import { Column } from "@ant-design/charts";
import {
  MeasureTableElementType,
  MeasureType,
  ObjectType,
} from "pages/admin/objectsAndClients/objects/types";
import {
  CheckCircleOutlined,
  EllipsisOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import useReportStore from "stores/admin/report";
import clsx from "clsx";

type ComponentProps = {
  selectedObject: ObjectType;
};

const Page: React.FC<ComponentProps> = ({ selectedObject }) => {
  const { object, setObject } = useReportStore((store) => store);

  useEffect(() => {
    setObject(selectedObject);
  }, [setObject, selectedObject]);

  const measuresList = useMemo(() => {
    if (!object) return [];

    return object.measures;
  }, [object]);

  const stagesList = useMemo(() => {
    if (!object) return [];

    return object.stages;
  }, [object]);

  const setMeasuresList = useCallback(
    (setMeasuresListFunction: (state: MeasureType[]) => MeasureType[]) => {
      setObject((object) => {
        if (!object) return object;

        return {
          ...object,
          measures: setMeasuresListFunction(object.measures),
        };
      });
    },
    [setObject]
  );

  const setStagesList = useCallback(
    (
      setStagesListFunction: (
        state: ObjectType["stages"]
      ) => ObjectType["stages"]
    ) => {
      setObject((object) => {
        if (!object) return object;

        return {
          ...object,
          stages: setStagesListFunction(object.stages),
        };
      });
    },
    [setObject]
  );

  const handleChangeChildrenVisibility = useCallback(
    ({
      type,
      id,
    }: {
      type: "group" | "building" | "element";
      id: string | number;
    }) => {
      if (type === "element") return;

      setMeasuresList((measuresList) => {
        return measuresList.map((building) => {
          return {
            ...building,
            is_children_visible:
              type === "building" && id === building.id
                ? !building?.is_children_visible
                : !!building?.is_children_visible,
            groups: (building.groups ? building.groups : []).map((group) => {
              return {
                ...group,
                is_children_visible:
                  type === "group" && id === group.id
                    ? !group.is_children_visible
                    : !!group.is_children_visible,
              };
            }),
          };
        });
      });
    },
    [setMeasuresList]
  );

  const handleExpandOperations = useCallback(
    (stageId: number, value: boolean) => {
      setStagesList((stagesList) => {
        return stagesList.map((stage) => {
          if (stage.id !== stageId) return stage;

          return { ...stage, is_operations_expanded: value };
        });
      });
    },
    [setStagesList]
  );

  //  Set columns for table
  const columns = useMemo(() => {
    const result: ColumnsType = [
      {
        title: "Наименование",
        key: "name",
        onHeaderCell: () => ({
          style: { verticalAlign: "top", textAlign: "left" },
        }),
        width: "15%",
        render: (text: any, untypedRecord: unknown, index) => {
          const record = untypedRecord as MeasureTableElementType;
          const styles = record.styles;

          const { isHasChildren } = {
            element: () => {
              return {
                isHasChildren: false,
              };
            },
            group: () => {
              const currentGroup = measuresList
                .flatMap((building) => building.groups || [])
                .find((group) => group.id.toString() === record.id.toString());

              const isHasChildren = !!currentGroup?.elements?.length;

              return {
                isHasChildren,
              };
            },
            building: () => {
              const currentBuilding = measuresList.find((building) => {
                return building.id.toString() === record.id.toString();
              });

              const isHasChildren = !!currentBuilding?.groups?.length;

              return { isHasChildren };
            },
            default: () => {
              return {
                isHasChildren: false,
              };
            },
          }[record.type || "default"]();

          const type = record.type;

          return (
            <Space
              size="small"
              className={clsx(
                styles?.objectName,
                !!(record.type === "group") && styles?.group,
                !!(record.type === "element") && styles?.element
              )}
              onClick={() =>
                handleChangeChildrenVisibility({
                  type: type,
                  id: record.id,
                })
              }
            >
              {!!isHasChildren ? (
                <>
                  {record.is_children_visible ? (
                    <MinusOutlined
                      className={clsx(
                        styles?.minus,
                        record.type === "element" && styles?.elementIcon
                      )}
                    />
                  ) : (
                    <PlusOutlined
                      className={clsx(
                        styles?.plus,
                        record.type === "element" && styles?.elementIcon
                      )}
                    />
                  )}
                </>
              ) : (
                <div
                  className={clsx(
                    styles?.empty,
                    record.type === "element" && styles?.elementIcon
                  )}
                ></div>
              )}
              {record.name}
            </Space>
          );
        },
      },
      {
        title: "Комментарий",
        key: "comment",
        width: "15%",
        onHeaderCell: () => ({
          style: { verticalAlign: "top", textAlign: "left" },
        }),
        render: (text: any, untypedRecord: unknown, index) => {
          const record = untypedRecord as MeasureTableElementType;

          return (
            <div>
              <Space size="small">{record.comment}</Space>
            </div>
          );
        },
      },
    ];

    stagesList.forEach((stage) => {
      if (stage.status === "unactive") return;

      if (!stage?.is_operations_expanded) {
        result.push({
          title: (
            <>
              <div className={styles.stageTitle}>
                {stage.short_name ? stage.short_name : stage.name}
              </div>
              <Button
                type="primary"
                size="small"
                className={styles.expandButton}
                onClick={() => handleExpandOperations(stage.id, true)}
              >
                Развернуть
              </Button>
            </>
          ),
          key: `stage-notExpandex-${stage.id}`,
          onHeaderCell: () => ({
            style: { verticalAlign: "top", textAlign: "left" },
          }),
          render: (text: any, untypedRecord: unknown, index) => {
            const record = untypedRecord as MeasureTableElementType;

            const { isChecked, isSemiChecked } = {
              element: () => {
                if (!record.operations)
                  return {
                    isChecked: false,
                    isSemiChecked: false,
                  };

                const isChecked = record.operations?.[stage.id]
                  ? !!record.operations[stage.id].every((recordOperation) => {
                      if (!recordOperation.value) return false;

                      return true;
                    })
                  : false;

                const isSemiChecked = isChecked
                  ? false
                  : record.operations?.[stage.id]
                  ? !!record.operations[stage.id].some((recordOperation) => {
                      if (!recordOperation.value) return false;

                      return true;
                    })
                  : false;

                return {
                  isChecked,
                  isSemiChecked,
                };
              },
              group: () => {
                const isChecked = measuresList.reduce(
                  (buildingResult, building) => {
                    if (!building.groups?.length) return buildingResult;

                    //  Поскольку проверяется только 1 group, если в результате был успех хоть раз, возвращаем true
                    if (buildingResult) return true;

                    return building.groups.reduce((groupResult, group) => {
                      if (group.id.toString() !== record.id.toString())
                        return groupResult;

                      if (!group.elements?.length) return groupResult;

                      //  Поскольку выше сравнение с record.id, проверен будет 1 group
                      return !!group.elements.every((element) => {
                        return !!stagesList
                          .filter((stageItem) => stageItem.status === "active")
                          .reduce((result, stageItem) => {
                            if (result) return true;

                            if (stageItem.id.toString() !== stage.id.toString())
                              return result;

                            return element.operations?.[stage.id]
                              ? !!element.operations[stage.id].every(
                                  (elementOperation) => {
                                    if (!elementOperation.value) return false;

                                    return true;
                                  }
                                )
                              : false;
                          }, false);
                      });
                    }, false);
                  },
                  false
                );

                const isSemiChecked = isChecked
                  ? false
                  : measuresList.reduce((buildingResult, building) => {
                      if (!building.groups?.length) return buildingResult;

                      //  Поскольку проверяется только 1 group, если в результате был успех хоть раз, возвращаем true
                      if (buildingResult) return true;

                      //  Поскольку выше сравнение с record.id, проверен будет 1 group
                      return building.groups.reduce((groupResult, group) => {
                        if (group.id.toString() !== record.id.toString())
                          return groupResult;

                        if (!group.elements?.length) return groupResult;

                        return !!group.elements.some((element) => {
                          return !!stagesList
                            .filter(
                              (stageItem) => stageItem.status === "active"
                            )
                            .reduce((result, stageItem) => {
                              if (result) return true;

                              if (
                                stageItem.id.toString() !== stage.id.toString()
                              )
                                return result;

                              return element.operations?.[stage.id]
                                ? !!element.operations[stage.id].some(
                                    (elementOperation) => {
                                      if (!elementOperation.value) return false;

                                      return true;
                                    }
                                  )
                                : false;
                            }, false);
                        });
                      }, false);
                    }, false);

                return {
                  isChecked,
                  isSemiChecked,
                };
              },
              building: () => {
                const isChecked = measuresList.reduce(
                  (buildingResult, building) => {
                    if (!building.groups?.length) return buildingResult;

                    if (building.id.toString() !== record.id.toString())
                      return buildingResult;

                    if (
                      !building.groups.some((group) => !!group.elements?.length)
                    )
                      return buildingResult;

                    return building.groups.every((group) => {
                      if (!group.elements?.length) return true;

                      return !!group.elements.every((element) => {
                        return !!stagesList
                          .filter((stageItem) => stageItem.status === "active")
                          .reduce((result, stageItem) => {
                            if (result) return true;

                            if (stageItem.id.toString() !== stage.id.toString())
                              return result;

                            return element.operations?.[stage.id]
                              ? !!element.operations[stage.id].every(
                                  (elementOperation) => {
                                    if (!elementOperation.value) return false;

                                    return true;
                                  }
                                )
                              : false;
                          }, false);
                      });
                    }, false);
                  },
                  false
                );

                const isSemiChecked = isChecked
                  ? false
                  : measuresList.reduce((buildingResult, building) => {
                      if (!building.groups?.length) return buildingResult;

                      if (building.id.toString() !== record.id.toString())
                        return buildingResult;

                      if (
                        !building.groups.some(
                          (group) => !!group.elements?.length
                        )
                      )
                        return buildingResult;

                      return building.groups.some((group) => {
                        if (!group.elements?.length) return false;

                        return !!group.elements.some((element) => {
                          return !!stagesList
                            .filter(
                              (stageItem) => stageItem.status === "active"
                            )
                            .reduce((result, stageItem) => {
                              if (result) return true;

                              if (
                                stageItem.id.toString() !== stage.id.toString()
                              )
                                return result;

                              return element.operations?.[stage.id]
                                ? !!element.operations[stage.id].some(
                                    (elementOperation) => {
                                      if (!elementOperation.value) return false;

                                      return true;
                                    }
                                  )
                                : false;
                            }, false);
                        });
                      }, false);
                    }, false);

                return { isChecked, isSemiChecked };
              },
              default: () => {
                return {
                  isChecked: false,
                  isSemiChecked: false,
                };
              },
            }[record.type || "default"]();

            return (
              <div>
                <Space size="small">
                  {isChecked ? (
                    <CheckCircleOutlined className={styles.okGreen} />
                  ) : isSemiChecked ? (
                    <ClockCircleOutlined
                      className={styles.partlyCompletedYellow}
                    />
                  ) : (
                    <EllipsisOutlined className={styles.notCompletedGray} />
                  )}
                </Space>
              </div>
            );
          },
        });
      }

      if (stage?.is_operations_expanded) {
        const children: ColumnsType = [];

        if (stage.operations)
          stage.operations.forEach((operation) => {
            children.push({
              title: `${operation.name} (${operation.completion_percentage}%)`,
              key: `stage-operation-${operation.id}`,
              render: (text: any, untypedRecord: unknown, index) => {
                const record = untypedRecord as MeasureTableElementType;

                const { isChecked, isSemiChecked } = {
                  element: () => {
                    if (!record.operations)
                      return {
                        isChecked: false,
                        isSemiChecked: false,
                      };

                    const isChecked = record.operations?.[stage.id]
                      ? !!record.operations[stage.id].find(
                          (recordOperation) => {
                            if (
                              recordOperation.stage_operation_id.toString() !==
                              operation.id.toString()
                            )
                              return false;

                            if (!recordOperation.value) return false;

                            return true;
                          }
                        )
                      : false;

                    return {
                      isChecked,
                      isSemiChecked: false,
                    };
                  },
                  group: () => {
                    const isChecked = measuresList.reduce(
                      (buildingResult, building) => {
                        if (!building.groups?.length) return buildingResult;

                        //  Поскольку проверяется только 1 group, если в результате был успех хоть раз, возвращаем true
                        if (buildingResult) return true;

                        return building.groups.reduce((groupResult, group) => {
                          if (group.id.toString() !== record.id.toString())
                            return groupResult;

                          if (!group.elements?.length) return groupResult;

                          //  Поскольку выше сравнение с record.id, проверен будет 1 group
                          return !!group.elements.every((element) => {
                            return !!stagesList
                              .filter(
                                (stageItem) => stageItem.status === "active"
                              )
                              .reduce((result, stageItem) => {
                                if (result) return true;

                                if (
                                  stageItem.id.toString() !==
                                  stage.id.toString()
                                )
                                  return result;

                                return element.operations?.[stage.id]
                                  ? !!element.operations[stage.id].find(
                                      (elementOperation) => {
                                        if (
                                          elementOperation.stage_operation_id.toString() !==
                                          operation.id.toString()
                                        )
                                          return false;

                                        if (!elementOperation.value)
                                          return false;

                                        return true;
                                      }
                                    )
                                  : false;
                              }, false);
                          });
                        }, false);
                      },
                      false
                    );

                    const isSemiChecked = isChecked
                      ? false
                      : measuresList.reduce((buildingResult, building) => {
                          if (!building.groups?.length) return buildingResult;

                          //  Поскольку проверяется только 1 group, если в результате был успех хоть раз, возвращаем true
                          if (buildingResult) return true;

                          //  Поскольку выше сравнение с record.id, проверен будет 1 group
                          return building.groups.reduce(
                            (groupResult, group) => {
                              if (group.id.toString() !== record.id.toString())
                                return groupResult;

                              if (!group.elements?.length) return groupResult;

                              return !!group.elements.some((element) => {
                                return !!stagesList
                                  .filter(
                                    (stageItem) => stageItem.status === "active"
                                  )
                                  .reduce((result, stageItem) => {
                                    if (result) return true;

                                    if (
                                      stageItem.id.toString() !==
                                      stage.id.toString()
                                    )
                                      return result;

                                    return element.operations?.[stage.id]
                                      ? !!element.operations[stage.id].find(
                                          (elementOperation) => {
                                            if (
                                              elementOperation.stage_operation_id.toString() !==
                                              operation.id.toString()
                                            )
                                              return false;

                                            if (!elementOperation.value)
                                              return false;

                                            return true;
                                          }
                                        )
                                      : false;
                                  }, false);
                              });
                            },
                            false
                          );
                        }, false);

                    return {
                      isChecked,
                      isSemiChecked,
                    };
                  },
                  building: () => {
                    const isChecked = measuresList.reduce(
                      (buildingResult, building) => {
                        if (!building.groups?.length) return buildingResult;

                        if (building.id.toString() !== record.id.toString())
                          return buildingResult;

                        if (
                          !building.groups.some(
                            (group) => !!group.elements?.length
                          )
                        )
                          return buildingResult;

                        return building.groups.every((group) => {
                          if (!group.elements?.length) return true;

                          return !!group.elements.every((element) => {
                            return !!stagesList
                              .filter(
                                (stageItem) => stageItem.status === "active"
                              )
                              .reduce((result, stageItem) => {
                                if (result) return true;

                                if (
                                  stageItem.id.toString() !==
                                  stage.id.toString()
                                )
                                  return result;

                                return element.operations?.[stage.id]
                                  ? !!element.operations[stage.id].find(
                                      (elementOperation) => {
                                        if (
                                          elementOperation.stage_operation_id.toString() !==
                                          operation.id.toString()
                                        )
                                          return false;

                                        if (!elementOperation.value)
                                          return false;

                                        return true;
                                      }
                                    )
                                  : false;
                              }, false);
                          });
                        }, false);
                      },
                      false
                    );

                    const isSemiChecked = isChecked
                      ? false
                      : measuresList.reduce((buildingResult, building) => {
                          if (!building.groups?.length) return buildingResult;

                          if (building.id.toString() !== record.id.toString())
                            return buildingResult;

                          if (
                            !building.groups.some(
                              (group) => !!group.elements?.length
                            )
                          )
                            return buildingResult;

                          return building.groups.some((group) => {
                            if (!group.elements?.length) return false;

                            return !!group.elements.some((element) => {
                              return !!stagesList
                                .filter(
                                  (stageItem) => stageItem.status === "active"
                                )
                                .reduce((result, stageItem) => {
                                  if (result) return true;

                                  if (
                                    stageItem.id.toString() !==
                                    stage.id.toString()
                                  )
                                    return result;

                                  return element.operations?.[stage.id]
                                    ? !!element.operations[stage.id].find(
                                        (elementOperation) => {
                                          if (
                                            elementOperation.stage_operation_id.toString() !==
                                            operation.id.toString()
                                          )
                                            return false;

                                          if (!elementOperation.value)
                                            return false;

                                          return true;
                                        }
                                      )
                                    : false;
                                }, false);
                            });
                          }, false);
                        }, false);

                    return { isChecked, isSemiChecked };
                  },
                  default: () => {
                    return {
                      isChecked: false,
                      isSemiChecked: false,
                    };
                  },
                }[record.type || "default"]();

                return (
                  <div>
                    <Space size="small">
                      {isChecked ? (
                        <CheckCircleOutlined className={styles.okGreen} />
                      ) : isSemiChecked ? (
                        <ClockCircleOutlined
                          className={styles.partlyCompletedYellow}
                        />
                      ) : (
                        <EllipsisOutlined className={styles.notCompletedGray} />
                      )}
                    </Space>
                  </div>
                );
              },
            });
          });

        result.push({
          title: (
            <>
              <div className={styles.stageTitle}>
                {stage.short_name ? stage.short_name : stage.name}
              </div>
              <Button
                type="primary"
                size="small"
                className={styles.expandButton}
                onClick={() => handleExpandOperations(stage.id, false)}
              >
                Свернуть
              </Button>
            </>
          ),
          key: `stage-${stage.id}`,
          onHeaderCell: () => ({
            style: { verticalAlign: "top", textAlign: "left" },
          }),
          children: children,
        });
      }
    });

    return result;
  }, [
    measuresList,
    stagesList,
    handleChangeChildrenVisibility,
    handleExpandOperations,
  ]);

  const tableData = useMemo(() => {
    const result: MeasureTableElementType[] = [];

    measuresList.forEach((measure) => {
      const measureData = { ...measure, groups: undefined };

      const buildingUnits: MeasureTableElementType["units"] = [];
      const groups: MeasureTableElementType[] = [];

      if (measure?.groups) {
        measure.groups.forEach((group) => {
          const groupData = { ...group };

          const groupUnits: MeasureTableElementType["units"] = [];
          const elements: MeasureTableElementType[] = [];

          if (group?.elements) {
            group.elements.forEach((element) => {
              const elementData = { ...element };

              if (measure.is_children_visible)
                if (group.is_children_visible)
                  elements.push({
                    ...elementData,
                    type: "element",
                    index: `element-${elementData.id}`,
                    is_children_visible: false,
                  });

              if (elementData.units?.length) {
                elementData.units.forEach((unit) => {
                  const groupUnitIndex = groupUnits.findIndex((groupUnit) => {
                    return groupUnit.id === unit.id;
                  });

                  if (groupUnitIndex === -1) {
                    groupUnits.push({ ...unit });
                  }

                  if (groupUnitIndex !== -1) {
                    const groupUnitValue = groupUnits[groupUnitIndex].value;
                    const unitValue = unit.value;

                    groupUnits[groupUnitIndex].value = (
                      (groupUnitValue ? parseFloat(groupUnitValue) : 0) +
                      (unitValue ? parseFloat(unitValue) : 0)
                    )
                      .toFixed(2)
                      .toString();
                  }
                });
              }
            });
          }

          if (measure.is_children_visible)
            groups.push({
              ...groupData,
              type: "group",
              index: `group-${groupData.id}`,
              units: groupUnits?.length ? [...groupUnits] : undefined,
            });

          if (groupUnits?.length) {
            groupUnits.forEach((groupUnit) => {
              const buildingUnitIndex = buildingUnits.findIndex(
                (buildingUnit) => {
                  return buildingUnit.id === groupUnit.id;
                }
              );

              if (buildingUnitIndex === -1) {
                buildingUnits.push({ ...groupUnit });
              }

              if (buildingUnitIndex !== -1) {
                const buildingUnitValue =
                  buildingUnits[buildingUnitIndex].value;
                const unitValue = groupUnit.value;

                buildingUnits[buildingUnitIndex].value = (
                  (buildingUnitValue ? parseFloat(buildingUnitValue) : 0) +
                  (unitValue ? parseFloat(unitValue) : 0)
                )
                  .toFixed(2)
                  .toString();
              }
            });
          }

          groups.push(...elements);
        });
      }

      result.push({
        ...measureData,
        type: "building",
        index: `building-${measureData.id}`,
        units: buildingUnits?.length ? [...buildingUnits] : undefined,
      });

      result.push(...groups);
    });

    return result;
  }, [measuresList]);

  const chartConfig = useMemo(() => {
    if (!object) return {};

    const data: { percentage: number; total: number }[] = [];

    for (const date of Object.keys(object.costsGroupedByDate)) {
      data.push({
        percentage: object.costsGroupedByDate[date].cumulativeOperationSum,
        total: object.costsGroupedByDate[date].cumulativeInvoiceSum,
      });
    }

    return {
      data,
      xField: "percentage",
      yField: "total",
      yAxis: {
        max: object.chartMaxValue,
      },
      xAxis: {
        label: {
          formatter: (value: number) => `${value}%`,
        },
      },
    };
  }, [object]);

  return (
    <MobileInnerWrapper>
      <Table
        dataSource={tableData}
        columns={columns}
        rowKey="id"
        pagination={false}
        styles={styles}
      />
      {!!object && (
        <div>
          Статус завершения объекта:{" "}
          {object.totalCost === 0
            ? 0
            : (object.totalCompletedWorkCost / object.totalCost) * 100}
          %
        </div>
      )}
      {!!object && (
        <div>
          Статус затрат:{" "}
          {object.totalCost === 0
            ? 0
            : (object.cumulativeInvoiceSum / object.totalCost) * 100}
          %
        </div>
      )}
      {!!object?.chartMaxValue && <Column {...chartConfig} />}
    </MobileInnerWrapper>
  );
};

export default Page;
